import React from "react";
import { Link } from "react-router-dom";
import { ScrollText, Copy, Check, ShieldCheck, Hammer } from "lucide-react";
import { toast } from "sonner";
import { useGet } from "../../hooks/useApi.js";
import {
  copyToClipboard,
  type ScriptLibraryForTicketResponse,
} from "../../api/script-library.js";

/**
 * Script library card on the ticket detail page (issue #86 phase 1).
 *
 * Shows the approved library entries for the ticket's problem class so the
 * technician can copy the reviewed script and paste it into NinjaOne RMM
 * themselves. Renders nothing when the ticket has no confident problem-class
 * match or the class has no approved scripts - an honest absence, not a
 * guess. The <USER> placeholder stays intact; the tech sets it in the RMM.
 */
export function TicketScriptLibraryCard({ ticketId }: { ticketId: string }) {
  const { data, loading } = useGet<ScriptLibraryForTicketResponse>(
    `/api/v1/script-library/for-ticket/${ticketId}`,
  );
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const [openId, setOpenId] = React.useState<string | null>(null);

  if (loading || !data || !data.problemClass || data.items.length === 0) return null;

  async function copy(id: string, body: string) {
    const ok = await copyToClipboard(body);
    if (ok) {
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 2000);
      toast.success("Script copied - paste it into NinjaOne RMM");
    } else {
      toast.error("Could not copy to clipboard");
    }
  }

  return (
    <section aria-label="Script library" data-testid="script-library-card">
      <div className="mb-2 flex items-end justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-1.5 text-sm font-semibold">
            <ScrollText className="h-4 w-4 text-brand-500" />
            Script library
          </h3>
          <p className="text-xs text-[hsl(var(--content-subtle))]">
            Reviewed {data.label ?? data.problemClass} scripts. Copy into NinjaOne RMM to run -
            you make the call.
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {data.items.map((entry) => (
          <article
            key={entry.id}
            className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] p-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{entry.name}</span>
                  {entry.classification === "read_only" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                      <ShieldCheck className="h-3 w-3" /> read-only
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                      <Hammer className="h-3 w-3" /> remediation
                    </span>
                  )}
                  <span className="rounded-full bg-[hsl(var(--surface))] px-2 py-0.5 text-[10px] font-medium text-[hsl(var(--content-subtle))]">
                    v{entry.version}
                  </span>
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-[hsl(var(--content-subtle))]">
                  {entry.description}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <button
                  onClick={() => setOpenId((o) => (o === entry.id ? null : entry.id))}
                  className="rounded-lg border border-[hsl(var(--border))] px-2.5 py-1 text-xs font-semibold"
                >
                  {openId === entry.id ? "Hide" : "View"}
                </button>
                <button
                  onClick={() => copy(entry.id, entry.body)}
                  className="inline-flex items-center gap-1 rounded-lg bg-brand-600 px-2.5 py-1 text-xs font-semibold text-white"
                  aria-label={`Copy ${entry.name}`}
                >
                  {copiedId === entry.id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copiedId === entry.id ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
            {openId === entry.id && (
              <div className="mt-2">
                {entry.parameters.length > 0 && (
                  <p className="mb-1 text-[11px] text-[hsl(var(--content-subtle))]">
                    Parameters:{" "}
                    {entry.parameters.map((p) => (
                      <code key={p.name} className="mr-2 rounded bg-[hsl(var(--surface))] px-1 py-0.5">
                        &lt;{p.name.toUpperCase()}&gt;{p.description ? ` - ${p.description}` : ""}
                      </code>
                    ))}
                  </p>
                )}
                <pre className="max-h-64 overflow-auto rounded-lg bg-slate-950 p-3 text-xs text-slate-100">
                  <code>{entry.body}</code>
                </pre>
              </div>
            )}
          </article>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-[hsl(var(--content-subtle))]">
        Manage the library in{" "}
        <Link to="/admin/script-library" className="text-brand-600 hover:underline dark:text-brand-400">
          Admin → Script Library
        </Link>
        .
      </p>
    </section>
  );
}
